/**
 * Formatting and shared formulas — the ONLY place in the codebase that may use `Intl` /
 * `toLocaleString` (ESLint rule, engineering.md §4.1). PMAI had ~27 local copies of such helpers.
 */

export const DEFAULT_LOCALE = 'lv-LV';
export const DEFAULT_CURRENCY = 'EUR';
export const EMPTY = '–';
export const BUSINESS_TIMEZONE = 'Europe/Riga';

type Numeric = number | string | null | undefined;

function toNumber(value: Numeric): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

const moneyFormatters = new Map<string, Intl.NumberFormat>();
function moneyFormatter(currency: string): Intl.NumberFormat {
  let f = moneyFormatters.get(currency);
  if (!f) {
    f = new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    moneyFormatters.set(currency, f);
  }
  return f;
}

const decimalFormatter = new Intl.NumberFormat(DEFAULT_LOCALE, { maximumFractionDigits: 2 });
const integerFormatter = new Intl.NumberFormat(DEFAULT_LOCALE, { maximumFractionDigits: 0 });
const oneDecimalFormatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const dateFormatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: BUSINESS_TIMEZONE,
});
const dateTimeFormatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: BUSINESS_TIMEZONE,
});
const monthFormatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, { month: 'long', timeZone: 'UTC' });
const dayMonthFormatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  timeZone: BUSINESS_TIMEZONE,
});
const isoDateFormatter = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: BUSINESS_TIMEZONE,
});
const collator = new Intl.Collator(DEFAULT_LOCALE, { sensitivity: 'base', numeric: true });

/** Normalises the various Unicode spaces ICU uses (NBSP, narrow NBSP) to a regular space. */
function plainSpaces(s: string): string {
  return s.replace(/[\u00a0\u202f]/g, ' ');
}

export function formatMoney(value: Numeric, currency: string = DEFAULT_CURRENCY): string {
  const n = toNumber(value);
  return n === null ? EMPTY : plainSpaces(moneyFormatter(currency).format(n));
}

/** "€1,5k" from 1000 up, otherwise "€950" — narrow cells (Roadmap, FR-OVR-015). */
export function formatCompactMoney(value: Numeric): string {
  const n = toNumber(value);
  if (n === null) return EMPTY;
  return Math.abs(n) >= 1000
    ? `€${plainSpaces(oneDecimalFormatter.format(n / 1000))}k`
    : `€${plainSpaces(integerFormatter.format(n))}`;
}

export function formatNumber(value: Numeric): string {
  const n = toNumber(value);
  return n === null ? EMPTY : plainSpaces(decimalFormatter.format(n));
}

export function formatHours(value: Numeric): string {
  const n = toNumber(value);
  return n === null ? EMPTY : `${plainSpaces(decimalFormatter.format(n))} h`;
}

export function formatPercent(value: Numeric): string {
  const n = toNumber(value);
  return n === null ? EMPTY : `${plainSpaces(integerFormatter.format(n))} %`;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d === null ? EMPTY : dateFormatter.format(d);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d === null ? EMPTY : plainSpaces(dateTimeFormatter.format(d));
}

/** "dd.mm" — compact date (note list, FR-PRJ-028). */
export function formatDayMonth(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d === null ? EMPTY : dayMonthFormatter.format(d).replace(/\.$/, '');
}

/** "dd.mm hh:mm" — compact date and time (Last Updated, FR-DASH-010). */
export function formatShortDateTime(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d === null ? EMPTY : `${formatDayMonth(d)} ${formatDateTime(d).slice(-5)}`;
}

/** Today's calendar date in the business timezone, `YYYY-MM-DD`. */
export function todayIso(now: Date = new Date()): string {
  return isoDateFormatter.format(now);
}

/** Case- and accent-insensitive text order (lists sorted "A→Z", FR-DASH-007). */
export function compareText(a: string | null | undefined, b: string | null | undefined): number {
  return collator.compare(a ?? '', b ?? '');
}

/** "Oktobris" — month 1–12, capitalised Latvian name. */
export function formatMonthName(month: number): string {
  if (!Number.isInteger(month) || month < 1 || month > 12)
    throw new RangeError(`month 1-12 expected, got ${month}`);
  const name = monthFormatter.format(new Date(Date.UTC(2000, month - 1, 1)));
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** "Oktobris 2026" */
export function formatYearMonth(ym: { year: number; month: number }): string {
  return `${formatMonthName(ym.month)} ${ym.year}`;
}

/** "Okt 26" — narrow column headers (Roadmap, FR-OVR-013). */
export function formatShortYearMonth(ym: { year: number; month: number }): string {
  return `${formatMonthName(ym.month).slice(0, 3)} ${String(ym.year).slice(2)}`;
}

/**
 * Profit margin in percent — the single definition (decision Q14, 2026-10-12):
 * round(invoiced / costs × 100); 0 when costs are 0. Used by timesheet summary, project
 * indicators and overview sorting alike.
 */
export function profitMargin(invoiced: Numeric, costs: Numeric): number {
  const i = toNumber(invoiced) ?? 0;
  const c = toNumber(costs) ?? 0;
  return c === 0 ? 0 : Math.round((i / c) * 100);
}
